import { useTranslation } from 'next-i18next';
import { BaseLayout } from '@hub/layout/BaseLayout/BaseLayout';
import type { IbhProduct } from '@hub/libs/bearing-hub.helper';
import { BearingHubHelper, SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import type { NodeContact } from '@hub/models/api/node-contact.types';
import React, { useEffect, useState } from 'react';
import { ProductDetailsUsp } from '@hub/bearing-hub/ProductDetails/ProductDetailsUsp/ProductDetailsUsp';
import { ProductDetailsBuyBox } from '@hub/bearing-hub/ProductDetails/ProductDetailsBuyBox/ProductDetailsBuyBox';
import { getLocale } from '@hub/libs/constants';
import { ProductDetailsTabs } from '@hub/bearing-hub/ProductDetails/ProductTabs/ProductDetailsTabs';
import { useRouter } from 'next/router';
import { createMarkup, stringToArray } from '@hub/libs/utils';
import classNames from 'classnames';
import styles from './ProductDetails.module.scss';
import type { LanguageAlternate } from '@hub/models/data';
import { SeoMeta } from '@hub/common/SeoMeta/SeoMeta';
import type { Teasers } from '@hub/generated/graphql';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import { IsFitBox } from '@hub/bearing-hub/ProductDetails/IsFitBox/IsFitBox';
import { LifetimeParametersModal } from '@hub/bearing-hub/libs/BearingsLifetime/LifetimeParametersModal';
import { useArticleLifeTime } from '@hub/bearing-hub/libs/BearingsLifetime/useArticleLifeTime';
import { useLifetimeParametersStore } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import type { IbhQueryParams } from '@hub/bearing-hub/BearingHubPage/libs/queryParamsHandler';
import { queryParamsHandler } from '@hub/bearing-hub/BearingHubPage/libs/queryParamsHandler';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { LifeTimePopUpInfo } from '@hub/bearing-hub/libs/BearingsLifetime/LifeTimePopUpInfo';
import type { SuitableProductProps } from '@hub/common/SuitableProduct/SuitableProduct';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import { getCanonicalLink } from '@hub/bearing-hub/common/link.factory';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { getEventCategory } from '@hub/libs/tracking';
import { TrackedIbhEventActions } from '@hub/bearing-hub/common/tracking.config';
import { IbhOppositeMaterialIds } from '@igus/iglidur-bearing-hub-api';
import type {
  IbhCatalogArticleResponseDto,
  IbhCustomArticleResponseDto,
  IbhPartsResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { useArticle } from '@hub/bearing-hub/ProductDetails/ArticleData/useArticle';
import { DEFAULT_QUANTITY } from '@hub/bearing-hub/common/constants';
import { ProductMedia } from '@hub/bearing-hub/libs/bearingProductMedia/productMedia';
import { useProductMedia } from '@hub/bearing-hub/libs/bearingProductMedia/useProductMedia';
import type { TechnicalDataCategory } from '@hub/common/ProductTabs/tabs/SuitableProductsTab';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { UnitSystem } from '@igus/kopla-domain';
import type { LayoutData } from '@hub/page-helpers/getInitialLayoutData';
import type { LifetimeParameters } from '@hub/bearing-hub/common/types';

export interface ProductDetailPageProps {
  languageAlternates: LanguageAlternate[];
  onlineToolsDefinitions: Teasers[];
  selectedShape: ShapeDescriptor;
  layout: LayoutData;
  contact: NodeContact | null;
  articleNumber: string;
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto;
  akeneoProductData?: IbhProduct;
  dimensions: IbhDimensions;
  isCustom: boolean;
  technicalDataCategories: TechnicalDataCategory[];
  suitableProducts: SuitableProductProps[];
  similarParts: IbhPartsResponseDto[];
  productTitle: string;
  metaTitle: string;
  metaDescription: string;
  materialDescription?: string;
}

export const ProductDetails = ({
  languageAlternates,
  onlineToolsDefinitions,
  layout,
  selectedShape,
  contact,
  articleNumber,
  articleData,
  akeneoProductData,
  dimensions,
  isCustom,
  technicalDataCategories,
  suitableProducts,
  similarParts,
  productTitle,
  metaTitle,
  metaDescription,
  materialDescription,
}: ProductDetailPageProps) => {
  const router = useRouter();
  const locale = getLocale(router);
  const { t } = useTranslation();
  const { CustomEvent } = usePiwikPro();
  const {
    lifetimeParameters,
    getLifetimeConfigPayload,
    hasLifetimeParameterValues,
    formUnitSystem,
    setFormUnitSystem,
    resetLifetimeParametersToDefault,
  } = useLifetimeParametersStore();
  const session = useSessionStore(state => state.session);
  const hashQuery = useNextHashQueryParams();
  const [currentQuantity, setCurrentQuantity] = useState(Number(hashQuery.quantity) || DEFAULT_QUANTITY);
  const [lifeTimeModalOpen, setLifeTimeModalOpen] = useState(false);
  const [activeLifeTimePopUp, setActiveLifeTimePopUp] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [articleThumbnail, setArticleThumbnail] = useState<string | null>(null);
  const [shouldRecalculateLifeTime, setShouldRecalculateLifeTime] = useState(false);
  const [lifetimeParamsInitialized, setLifetimeParamsInitialized] = useState(false);
  const unitSystem = articleData.unitSystem as unknown as UnitSystem;
  const { article, articleIsLoading } = useArticle({
    articleNumber,
    isCustom,
    quantity: currentQuantity,
    shapeKey: selectedShape.shapeKey,
    session,
    unitSystem,
    dimensions,
    initialArticleData: articleData,
  });
  const { material, productionMethod, widens, widenShortKey, deliveryTimeCode, deliveryTimeText } = article;
  const originIsFromQr = router.query.origin === 'QR';
  const eventCategory = getEventCategory(router.asPath);
  const lifetimeConfigPayload = getLifetimeConfigPayload(true);
  const {
    lifetime,
    recalculateLifeTime,
    isFetching: lifetimeIsFetching,
  } = useArticleLifeTime(
    !!session && lifetimeParamsInitialized,
    {
      ...(isCustom
        ? {
            sid: session?.sessionId,
            server: session?.server,
            quantity: 1,
            articleNumber,
            d1: dimensions.d1,
            unitSystem,
            shape: selectedShape.shapeKey,
            ...dimensions,
          }
        : {
            articleNumber,
          }),
      ...lifetimeConfigPayload,
      oppositeMaterialId: Object.values(IbhOppositeMaterialIds).find(
        id => id === lifetimeParameters.oppositeMaterialId
      ),
      typeOfMovement: lifetimeParameters.typeOfMovement,
      temporalTypeOfMovement:
        'temporalTypeOfMovement' in lifetimeConfigPayload ? lifetimeParameters.temporalTypeOfMovement : undefined,
    },
    shouldRecalculateLifeTime,
    productionMethod
  );
  const productMedia = useProductMedia({
    widens,
    title: akeneoProductData?.title || material.name,
    widenShortKey: widenShortKey || selectedShape.widenShortKeyIcon,
    material,
    shape: selectedShape,
  });
  let onlineToolsList = akeneoProductData?.onlineTools;

  if (typeof onlineToolsList === 'string') {
    try {
      onlineToolsList = stringToArray(onlineToolsList);
    } catch (error) {
      onlineToolsList = [];
    }
  }

  const onlineTools = onlineToolsDefinitions.filter(
    teaser => !!onlineToolsList?.find(onlineTool => onlineTool === teaser.id)
  );

  const mainIbhInfoPagePath = BearingHubHelper.getMainIbhInfoPagePath(locale);

  const hash = queryParamsHandler.createHashParam(hashQuery).toString();
  const breadcrumbItems = [
    {
      isExternal: true,
      title: t(`GOTO_CATEGORY_PAGE`, { ns: 'bearing-hub/bearingHub' }),
      url: mainIbhInfoPagePath,
      key: mainIbhInfoPagePath,
    },
    {
      title: t(`SHAPES.${selectedShape.shapeKey}.HEADLINE`, { ns: 'bearing-hub/bearingHub' }),
      url: {
        pathname: `/${SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'}/${
          selectedShape.shapePath
        }`,
        hash,
      },
      parentKeys: [mainIbhInfoPagePath],
    },
    {
      title: articleNumber.replace('TEMP-', ''),
      url: null,
      parentKeys: [
        `/${SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'}/${selectedShape.shapePath}`,
      ],
    },
  ];
  const canonicalLink = getCanonicalLink(languageAlternates, locale);

  const updateResultPageQueryParams = (newParameters: Partial<IbhQueryParams>) => {
    const newQuery = {
      ...router.query,
      ...hashQuery,
      ...newParameters,
    } as IbhQueryParams;

    queryParamsHandler.updateRouterQuery(router, newQuery);
  };

  const updateFormUnitSystemQueryParam = (
    unitSystemValue: UnitSystem,
    convertedLifetimeParameters: LifetimeParameters
  ) => {
    updateResultPageQueryParams({
      formUnitSystem: queryParamsHandler.getFormUnitSystemParam(unitSystemValue),
      lifetimeParameters: queryParamsHandler.getServiceLifeParams(convertedLifetimeParameters, unitSystemValue),
    });
  };

  const updateQuantityChange = (value: number) => {
    updateResultPageQueryParams({ quantity: `${value}` });
    setCurrentQuantity(value);
  };

  const onCalculateLifeTime = () => {
    updateResultPageQueryParams({
      formUnitSystem: queryParamsHandler.getFormUnitSystemParam(formUnitSystem),
      lifetimeParameters: queryParamsHandler.getServiceLifeParams(lifetimeParameters, formUnitSystem),
    });
    if (hasLifetimeParameterValues()) {
      void recalculateLifeTime();
    }
  };

  const trackUserOrigin = () => {
    if (originIsFromQr && eventCategory) {
      CustomEvent.trackEvent(eventCategory, TrackedIbhEventActions.articleSelectQR, articleNumber);
    }
  };

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    const quantityFromHash = hashQuery.quantity;
    const defaultFormUnitSystem = hashQuery.lifetimeParameters ? UnitSystem.metric : unitSystem;
    setFormUnitSystem(queryParamsHandler.parseFormUnitSystem(hashQuery.formUnitSystem, defaultFormUnitSystem));
    resetLifetimeParametersToDefault(queryParamsHandler.getDataFromParams(hashQuery.lifetimeParameters as string));
    if (Number(quantityFromHash) >= 1) {
      setCurrentQuantity(Number(quantityFromHash));
    }
    setShouldRecalculateLifeTime(!!hashQuery.lifetimeParameters);
    setLifetimeParamsInitialized(true);
  }, [router.isReady]);

  useEffect(() => {
    trackUserOrigin();
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setLifeTimeModalOpen(false);
  }, [lifetime]);

  const marketingCloudTags = [
    {
      name: 'sis-tag_page-type',
      content: 'Shop',
    },
    {
      name: 'sis-tag_page-subtype',
      content: 'Product Detail Page',
    },
    {
      name: 'sis-tag_article-number',
      content: articleNumber,
    },
    {
      name: 'sis-tag_availability-code',
      content: deliveryTimeCode.toString(),
    },
    {
      name: 'sis-tag_availability-text',
      content: deliveryTimeText,
    },
  ];

  const productDetailsSection = (
    <div className="w-full">
      <ProductDetailsUsp
        selectedShape={selectedShape}
        lifetimeParameters={lifetimeParameters}
        articleData={articleData}
        quantity={currentQuantity}
        locale={locale}
        lifetime={lifetime}
        formUnitSystem={formUnitSystem}
        setActiveLifeTimePopUp={setActiveLifeTimePopUp}
      />
      {akeneoProductData?.usp && (
        <>
          <span className="font-bold pb-4">
            {t(`HEADLINES.MATERIAL_PROPERTIES`, { ns: 'bearing-hub/bearingHub' })}:{' '}
          </span>
          <div
            className={`${classNames(styles.usp)} pl-8 pt-4`}
            dangerouslySetInnerHTML={createMarkup(akeneoProductData?.usp)}
          />
        </>
      )}
    </div>
  );

  const renderPageContent = () => (
    <>
      <h1 className={`mt-10 leading-5xl ${!akeneoProductData?.singleUsp ? 'mb-10' : ''}`}>{productTitle}</h1>
      {akeneoProductData?.singleUsp && (
        <h2 className="!mb-10 text-sm" dangerouslySetInnerHTML={createMarkup(akeneoProductData?.singleUsp)} />
      )}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 igus-text-styles main-content-grid"
        data-testid="page-layout-default"
      >
        <ProductMedia
          produceProductMedia={productMedia}
          articleNumber={articleNumber}
          dimensions={dimensions}
          isCustom={isCustom}
          productionMethod={productionMethod}
          shapeKey={selectedShape.shapeKey}
          unitSystem={unitSystem}
          materialColor={material?.colorHex}
          onModelThumbnailGenerate={setArticleThumbnail}
        />
        {productDetailsSection}
        {isMounted && (
          <div>
            <ProductDetailsBuyBox
              articleData={article}
              quantity={currentQuantity}
              setupCostIsLoading={articleIsLoading}
              articleIsCustom={isCustom}
              session={session}
              articleThumbnail={articleThumbnail}
              onQuantityChange={value => updateQuantityChange(value)}
            />
          </div>
        )}
        <div className="sm:col-span-2">
          <ProductDetailsTabs
            articleData={articleData}
            productDetails={materialDescription || ''}
            technicalDataCategories={technicalDataCategories}
            suitableProducts={suitableProducts}
            selectedShape={selectedShape}
            onlineTools={onlineTools}
            isCustom={isCustom}
            dimensions={dimensions}
            quantity={currentQuantity}
            similarParts={similarParts}
            unitSystem={unitSystem}
            session={session}
          />
        </div>
        <div className="row-start-4 sm:row-start-3 lg:row-start-2 lg:col-start-3">
          <IsFitBox
            articleNumber={articleNumber}
            isCustom={isCustom}
            onServiceLifeModalOpen={setLifeTimeModalOpen}
            articleTitle={akeneoProductData?.title}
          />
        </div>
      </div>
    </>
  );

  return (
    <BaseLayout breadcrumbItems={breadcrumbItems} contact={contact} layout={layout}>
      <SeoMeta
        categoryName={selectedShape.shapePath}
        languageAlternates={languageAlternates}
        totalPages={1}
        productImage={BearingHubHelper.getWidenImageUrl(widenShortKey, selectedShape.widenShortKeyIcon)}
        webTrendCGNKey="iglidur"
        metaTitle={metaTitle}
        metaDescription={metaDescription}
        canonical={canonicalLink}
        noIndexState={isCustom}
        noFollow={isCustom}
        marketingCloudTags={marketingCloudTags}
      />
      <LifetimeParametersModal
        onLifeTimeModalClose={setLifeTimeModalOpen}
        visible={lifeTimeModalOpen}
        onCalculateLifeTime={onCalculateLifeTime}
        articleNumber={articleNumber.replace('TEMP-', '')}
        unitSystem={unitSystem}
        shapeKey={selectedShape.shapeKey}
        onReset={() => resetLifetimeParametersToDefault()}
        onFormUnitSystemChange={updateFormUnitSystemQueryParam}
        lifetimeIsLoading={lifetimeIsFetching}
      />
      <LifeTimePopUpInfo activeModal={activeLifeTimePopUp} setActiveModal={value => setActiveLifeTimePopUp(value)} />
      {renderPageContent()}
    </BaseLayout>
  );
};
